///
/// EarthBrowser
///
/// Created by Matt Giger on September 23, 2026
/// Copyright (c) 2026 Geodata Labs
///
/// Sign-in / activity header for the Users section: activity tiles (from
/// tenure tracking, meaningful immediately) plus a 30-day sign-in bar
/// chart and method breakdown (from profile_signin_log, which starts
/// accruing at deploy time).
///

import { Skeleton } from '@/components/ui/skeleton'
import { useGetSigninStatsQuery, type SigninStats } from '@/redux/api/userInsightsApi'
import { Activity, KeyRound, UserPlus, Users } from 'lucide-react'

function StatTile({ icon, label, value, sub }: {
  icon: React.ReactNode
  label: string
  value: string
  sub?: string
}) {
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl border border-border bg-card">
      <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400">{icon}</div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-semibold leading-tight">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </div>
  )
}

/** Last 30 calendar days with zero-filled gaps, oldest first */
function dailySeries(stats: SigninStats) {
  const byDay = new Map(stats.daily.map(d => [d.day.slice(0, 10), d]))
  const days: { day: string; signins: number; registrations: number; unique: number }[] = []
  for (let i = 29; i >= 0; i--) {
    const date = new Date(Date.now() - i * 86400_000)
    const key = date.toISOString().slice(0, 10)
    const row = byDay.get(key)
    days.push({
      day: key,
      signins: row?.signins ?? 0,
      registrations: row?.registrations ?? 0,
      unique: row?.unique_users ?? 0,
    })
  }
  return days
}

function methodLabel(method: string, provider: string | null): string {
  return provider ?? method
}

export function SigninStatsStrip() {
  const { data: stats, isLoading } = useGetSigninStatsQuery()

  if (isLoading || !stats) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}
      </div>
    )
  }

  const days = dailySeries(stats)
  const maxSignins = Math.max(1, ...days.map(d => d.signins))
  const total30d = days.reduce((sum, d) => sum + d.signins, 0)

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          icon={<Activity className="h-4 w-4" />}
          label="Active Today"
          value={stats.activity.active_today.toLocaleString()}
          sub={`${stats.activity.active_7d.toLocaleString()} in 7d · ${stats.activity.active_30d.toLocaleString()} in 30d`}
        />
        <StatTile
          icon={<KeyRound className="h-4 w-4" />}
          label="Sign-ins Today"
          value={stats.totals.signins_today.toLocaleString()}
          sub={`${stats.totals.unique_7d.toLocaleString()} unique users in 7d`}
        />
        <StatTile
          icon={<UserPlus className="h-4 w-4" />}
          label="Registrations (30d)"
          value={days.reduce((sum, d) => sum + d.registrations, 0).toLocaleString()}
        />
        <StatTile
          icon={<Users className="h-4 w-4" />}
          label="Total Users"
          value={stats.activity.total_users.toLocaleString()}
        />
      </div>

      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold">Sign-ins</h3>
          <span className="text-xs text-muted-foreground">last 30 days · {total30d.toLocaleString()} total</span>
          {stats.methods.length > 0 && (
            <span className="ml-auto flex flex-wrap gap-1.5">
              {stats.methods.map(m => (
                <span
                  key={`${m.method}:${m.provider ?? ''}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-border bg-background text-xs text-muted-foreground"
                >
                  {methodLabel(m.method, m.provider)}
                  <span className="font-semibold text-foreground">{m.count.toLocaleString()}</span>
                </span>
              ))}
            </span>
          )}
        </div>
        <div className="flex items-end gap-[2px] h-20" aria-label="Daily sign-ins, last 30 days">
          {days.map(d => (
            <div
              key={d.day}
              className="flex-1 flex flex-col justify-end h-full group"
              title={`${d.day}: ${d.signins} sign-in${d.signins === 1 ? '' : 's'} (${d.unique} unique${d.registrations > 0 ? `, ${d.registrations} new` : ''})`}
            >
              <div
                className={`rounded-sm min-h-[2px] transition-all duration-300 group-hover:opacity-80 ${d.signins > 0 ? 'bg-blue-500' : 'bg-muted'}`}
                style={{ height: `${Math.max(2, (d.signins / maxSignins) * 100)}%` }}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>{days[0].day}</span>
          <span>today</span>
        </div>
      </div>
    </div>
  )
}
